/**
 * ADD AREA MODAL
 * 
 * Modal for selecting area type and entering label
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { AREA_TYPES, getAreaType } from '../../constants/areaTypes';

const AddAreaModal = ({ isOpen, onClose, onAdd }) => {
  const [selectedType, setSelectedType] = useState('');
  const [label, setLabel] = useState('');

  const handleAdd = () => {
    if (!selectedType) return;
    
    const areaConfig = getAreaType(selectedType);
    onAdd(selectedType, label || areaConfig.label);
    setSelectedType('');
    setLabel('');
    onClose();
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    // Auto-fill label with default
    const config = getAreaType(type);
    setLabel(config.label);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-slate-900 border border-slate-700 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-emerald-400">Add Area</h3>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Area Type Selection */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">
              Area Type <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-4 gap-2">
              {AREA_TYPES.map(type => (
                <button
                  key={type.value}
                  onClick={() => handleTypeSelect(type.value)}
                  className={`
                    p-3 border rounded text-center transition-all
                    ${selectedType === type.value 
                      ? 'border-emerald-500 bg-emerald-500/10' 
                      : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                    }
                  `}
                >
                  <div className="text-2xl mb-1">{type.icon}</div>
                  <div className={`text-xs ${selectedType === type.value ? 'text-emerald-400' : 'text-slate-400'}`}>
                    {type.label}
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Area Label */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              Area Label <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Master Bathroom, Guest Bedroom"
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none"
            />
            <p className="text-xs text-slate-500 mt-1">
              Give this area a specific name to identify it
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-slate-200"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!selectedType || !label}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Area
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAreaModal;
